/*eslint-disable*/
import React, { useState, useEffect, useRef } from "react";
import "./Switch.css";
import FastAPIClient from "../../../client";
import config from "../../../config";
import { FormattedMessage, IntlProvider } from "react-intl";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import Throbber from "../../../style/Throbber";
import { useNetwork } from "../../NetworkProvider";
import { Box, Button } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { useOfflineRecommendations } from "../../OfflineContext";
import { useParams, useNavigate } from "react-router-dom";
import { FiBookOpen, FiMessageSquare } from "react-icons/fi";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { HiInformationCircle } from "react-icons/hi2";

// Define types for translations instead of direct imports
type Messages = Record<string, string>;

// We'll use require instead of import for JSON files
// This is a common workaround when resolveJsonModule isn't enabled
const messages_en: Messages = require("./translations/en.json");
const messages_pt: Messages = require("./translations/pt.json");
const messages_fr: Messages = require("./translations/fr.json");

const client = new FastAPIClient(config);
const messages = { en: messages_en, pt: messages_pt, fr: messages_fr };

interface SwitchProps {
    isOn: boolean;
    handleToggle: () => void;
    onColor: string;
}

// Simple Switch Component
const Switch: React.FC<SwitchProps> = ({ isOn, handleToggle, onColor }) => (
    <>
        <input
            checked={isOn}
            onChange={handleToggle}
            className="react-switch-checkbox"
            id="react-switch-new"
            type="checkbox"
        />
        <label
            style={{ background: isOn ? onColor : undefined }}
            className="react-switch-label"
            htmlFor="react-switch-new"
        >
            <span className="react-switch-button" />
        </label>
    </>
);

interface TaskChoice {
    id: string | number;
    value: string;
}

// More specific type for task item
interface TaskItem {
    task_id: string;
    // Using Record with unknown is safer than 'any'
    [key: string]: string | number | boolean | null | undefined | object;
}

interface CollapsibleProps {
    item: TaskItem;
    text: string;
    read: boolean;
    getRecommendations: (user_id: string, client_id: string) => Promise<void>;
    client_id: string;
    task_choices?: TaskChoice[];
    userId: string | number;
}

const NBAitem: React.FC<CollapsibleProps> = ({
    item,
    text,
    read,
    getRecommendations,
    client_id,
    task_choices,
    userId,
}) => {
    const useMediaQuery = (query) => {
        const [matches, setMatches] = useState(false);

        useEffect(() => {
            const media = window.matchMedia(query);
            setMatches(media.matches);

            const handler = (event) => setMatches(event.matches);
            media.addListener(handler);

            return () => media.removeListener(handler);
        }, [query]);

        return matches;
    };
    const { isOnline } = useNetwork();
    const isMdOrLarger = useMediaQuery("(min-width: 768px)");
    const [open, setOpen] = React.useState(false);

    const handleTooltipClose = () => {
        setOpen(false);
    };

    const handleTooltipOpen = () => {
        setOpen(true);
    };
    const [isAccepted, setAccepted] = useState<boolean>(true);
    const [selectedTask, setSelectedTask] = useState<string | null>("");
    const [locale, setLocale] = useState<string>(
        localStorage.getItem("language") || "en"
    );
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Function to parse text and extract explanation
    const parseTextWithExplanation = (text: string) => {
        const segments: { type: "text" | "explanation"; content: string }[] =
            [];
        const regex = /<exp>(.*?)<\/exp>/g;
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            // Add preceding text
            if (match.index > lastIndex) {
                segments.push({
                    type: "text",
                    content: text.substring(lastIndex, match.index),
                });
            }
            // Add explanation text
            segments.push({ type: "explanation", content: match[1] });
            lastIndex = regex.lastIndex;
        }

        // Add any remaining text after the last match
        if (lastIndex < text.length) {
            segments.push({ type: "text", content: text.substring(lastIndex) });
        }

        return segments;
    };

    const parsedTextSegments = parseTextWithExplanation(text);

    useEffect(() => {
        setLocale(localStorage.getItem("language") || "en");
    }, []);

    interface OfflineParams {
        accepted_at: string;
    }

    const updateNotifications = async (): Promise<void> => {
        setIsSubmitting(true);
        let offlineParams = {} as OfflineParams;
        if (!isOnline) {
            offlineParams = {
                accepted_at: new Date().toISOString(),
            };
        }
        const payload = {
            task_id: item.task_id,
            client_id: client_id,
            read: true,
            accepted: isAccepted,
            reason: !isAccepted ? selectedTask : null,
            user_id: userId,
            ...offlineParams,
        };
        await client.updateRecommendation(item.task_id, payload);
        await getRecommendations(userId.toString(), client_id);
        setIsSubmitting(false);
    };

    // Detect if desktop or mobile
    const isMobile = !isMdOrLarger; // true if mobile, false if desktop

    return (
        <IntlProvider
            locale={locale}
            messages={messages[locale as keyof typeof messages]}
        >
            <div className="text-black w-full">
                {isSubmitting && (
                    <div className="absolute top-1/2 left-1/2 z-10">
                        <Throbber />
                    </div>
                )}
                <div className="flex-col md:mr-5 w-full">
                    <div className="flex items-center w-full">
                        <div className="flex flex-row items-center text-black w-full">
                            <div className="text-sm mr-2 w-full">
                                {parsedTextSegments.map((segment, index) =>
                                    segment.type === "text" ? (
                                        <span
                                            key={index}
                                            dangerouslySetInnerHTML={{
                                                __html: segment.content,
                                            }}
                                        ></span>
                                    ) : isMobile ? (
                                        <ClickAwayListener
                                            onClickAway={handleTooltipClose}
                                            key={index}
                                        >
                                            <div
                                                style={{
                                                    display: "inline-block",
                                                }}
                                            >
                                                <Tooltip
                                                    title={segment.content}
                                                    arrow
                                                    open={open}
                                                    onClose={handleTooltipClose}
                                                    disableFocusListener
                                                    disableHoverListener
                                                    disableTouchListener
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            padding: 0,
                                                            marginLeft: "4px",
                                                            verticalAlign:
                                                                "middle",
                                                        }}
                                                        onClick={
                                                            handleTooltipOpen
                                                        }
                                                    >
                                                        <HiInformationCircle />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </ClickAwayListener>
                                    ) : (
                                        <Tooltip
                                            key={index}
                                            title={segment.content}
                                            arrow
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    padding: 0,
                                                    marginLeft: "4px",
                                                    verticalAlign: "middle",
                                                }}
                                            >
                                                <HiInformationCircle />
                                            </IconButton>
                                        </Tooltip>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 text-base">
                        <div className="flex flex-row items-center gap-4">
                            <Box>
                                <FormControl>
                                    <Select
                                        id="acceptance-status-select"
                                        value={
                                            isAccepted ? "accepted" : "declined"
                                        }
                                        onChange={(event) => {
                                            setAccepted(
                                                event.target.value ===
                                                    "accepted"
                                            );
                                            if (
                                                event.target.value ===
                                                "accepted"
                                            ) {
                                                setSelectedTask(null);
                                            }
                                        }}
                                        sx={{
                                            borderRadius: "9999px",

                                            "& .MuiOutlinedInput-notchedOutline":
                                                {
                                                    borderRadius: "9999px",
                                                    border: "none",
                                                },
                                            "& .MuiInputBase-root": {
                                                borderRadius: "9999px",
                                            },
                                            "& .MuiOutlinedInput-input": {
                                                borderRadius: "9999px",
                                                padding: "6px 12px",
                                                color: isAccepted
                                                    ? "#000000"
                                                    : "#FFFFFF",
                                                fontSize: "0.85rem",
                                            },
                                            backgroundColor: isAccepted
                                                ? "#C3FF36"
                                                : "#F94643",
                                            color: isAccepted
                                                ? "#000000"
                                                : "#FFFFFF",
                                            "& .MuiSvgIcon-root": {
                                                color: isAccepted
                                                    ? "#000000"
                                                    : "#FFFFFF",
                                            },
                                            "& .MuiSelect-select": {
                                                color: isAccepted
                                                    ? "#000000"
                                                    : "#FFFFFF",
                                                fontSize: "0.85rem",
                                            },
                                            "& .MuiList-root .MuiMenuItem-root":
                                                {
                                                    color: "inherit",
                                                    fontSize: "0.85rem",
                                                },
                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                {
                                                    border: "none",
                                                },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                {
                                                    border: "none",
                                                },
                                        }}
                                    >
                                        <MenuItem value="accepted">
                                            <FormattedMessage id="nba.accepted" />
                                        </MenuItem>
                                        <MenuItem value="declined">
                                            <FormattedMessage id="nba.declined" />
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <div className="flex-grow">
                                {!isAccepted && (
                                    <FormControl
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            maxWidth: 150,
                                            width: "100%",
                                            "& .MuiOutlinedInput-notchedOutline":
                                                {
                                                    borderRadius: "9999px",
                                                },
                                            "& .MuiInputBase-root": {
                                                borderRadius: "9999px",
                                            },
                                        }}
                                    >
                                        <Select
                                            id="task-select"
                                            value={selectedTask}
                                            displayEmpty
                                            inputProps={{
                                                "aria-label": "Without label",
                                            }}
                                            onChange={(event) =>
                                                setSelectedTask(
                                                    event.target.value
                                                )
                                            }
                                            sx={{
                                                fontSize: "0.75rem",
                                                "& .MuiOutlinedInput-input": {
                                                    padding: "8px 12px", // Adjust padding if needed due to smaller font
                                                },
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Choisir une raison</em>
                                            </MenuItem>
                                            {task_choices?.map((task) => (
                                                <MenuItem
                                                    key={task.id}
                                                    value={task.value}
                                                >
                                                    {task.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </div>

                            <Button
                                onClick={updateNotifications}
                                disabled={
                                    isSubmitting ||
                                    (!isAccepted && selectedTask === "")
                                }
                                variant="contained"
                                sx={{
                                    borderRadius: "9999px",
                                    backgroundColor: "#000000",
                                    color: "#FFFFFF",

                                    padding: "4px 12px",
                                    textTransform: "none",

                                    marginLeft: "auto",
                                    fontSize: "0.85rem",
                                    "&:hover": {
                                        backgroundColor: "#FFFFFF",
                                        color: "#000000",
                                        border: "1px solid #000000",
                                    },
                                    "&:disabled": {
                                        backgroundColor: "#E0E0E0",
                                        color: "#A0A0A0",
                                    },
                                }}
                            >
                                <FormattedMessage id="nba.submit" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </IntlProvider>
    );
};

export default NBAitem;
